<template>
  <div class="attribute-unit-picker" ref="root">
    <button
      type="button"
      ref="trigger"
      class="unit-trigger"
      :class="{ 'unit-trigger--empty': !effectiveUnitId }"
      @click="togglePopover"
    >
      <span v-if="effectiveUnitLabel">{{ effectiveUnitLabel }}</span>
      <span v-else class="has-text-grey-light">Njësia</span>
      <i class="fa fa-chevron-down ml-1 is-size-7"></i>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="popover"
        class="unit-popover unit-popover--teleported"
        :style="popoverStyle"
        @mousedown.stop
        @click.stop
      >
        <div class="unit-popover-search">
          <input
            v-model="search"
            class="input is-small"
            type="text"
            placeholder="Kërko njësi..."
          />
        </div>

        <div class="unit-list">
          <button
            type="button"
            class="unit-list-item"
            :class="{ active: effectiveUnitId === null }"
            @click="selectUnit(null)"
          >
            Pa njësi
          </button>
          <button
            v-for="unit in filteredUnits"
            :key="unit.id"
            type="button"
            class="unit-list-item"
            :class="{ active: Number(unit.id) === effectiveUnitId }"
            @click="selectUnit(unit.id)"
          >
            {{ unit.name || unit.label }}
          </button>
        </div>

        <div class="unit-popover-actions">
          <button
            v-if="!showCreate"
            type="button"
            class="button is-small is-link is-light"
            @click.stop.prevent="openCreate"
          >
            <i class="fa fa-plus mr-1"></i> Krijo njësi të re
          </button>
          <div v-else class="unit-create-row">
            <input
              ref="newUnitInput"
              v-model="newUnitName"
              class="input is-small"
              type="text"
              placeholder="Emri i njësisë"
              @keyup.enter="createUnit"
            />
            <button type="button" class="button is-small is-link" :disabled="saving" @click.stop.prevent="createUnit">
              Ruaj
            </button>
            <button type="button" class="button is-small" @click.stop.prevent="cancelCreate">Anulo</button>
          </div>

          <div v-if="effectiveUnitId && !showRename" class="unit-rename-row">
            <button type="button" class="button is-small is-light" @click.stop.prevent="startRename">
              <i class="fa fa-pencil mr-1"></i> Riemëro njësinë
            </button>
          </div>
          <div v-if="showRename" class="unit-create-row">
            <input
              ref="renameUnitInput"
              v-model="renameUnitName"
              class="input is-small"
              type="text"
              @keyup.enter="confirmRename"
            />
            <button type="button" class="button is-small is-link" :disabled="saving" @click.stop.prevent="confirmRename">
              Ruaj
            </button>
            <button type="button" class="button is-small" @click.stop.prevent="cancelRename">Anulo</button>
          </div>
          <p v-if="showRename" class="help is-size-7 unit-global-hint">
            Ndryshimi vlen për të gjithë sistemin.
          </p>
          <p v-if="deferSave" class="help is-size-7 unit-global-hint">
            Lidhja e njësisë me këtë atribut ruhet kur ruani kategorinë.
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import UnitsServices from "@/services/Unit";
import AttributeServices from "@/services/Attribute";

export default {
  props: {
    measuringUnit: {
      type: [Number, String, null],
      default: null,
    },
    defaultMeasuringUnit: {
      type: [Number, String, null],
      default: null,
    },
    measuringUnitName: {
      type: String,
      default: "",
    },
    units: {
      type: Array,
      default: () => [],
    },
    categoryId: {
      type: String,
      default: "",
    },
    attributeId: {
      type: String,
      default: "",
    },
    categoryRelatedEntity: {
      type: String,
      default: "products",
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    deferSave: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:measuringUnit", "units-changed"],
  data() {
    return {
      open: false,
      search: "",
      showCreate: false,
      showRename: false,
      newUnitName: "",
      renameUnitName: "",
      saving: false,
      localUnits: [],
      pendingUnitLabel: "",
      popoverStyle: {},
    };
  },
  computed: {
    effectiveUnitId() {
      if (this.measuringUnit !== null && this.measuringUnit !== undefined && this.measuringUnit !== "") {
        return Number(this.measuringUnit);
      }
      if (
        this.defaultMeasuringUnit !== null &&
        this.defaultMeasuringUnit !== undefined &&
        this.defaultMeasuringUnit !== ""
      ) {
        return Number(this.defaultMeasuringUnit);
      }
      return null;
    },
    effectiveUnitLabel() {
      if (!this.effectiveUnitId) return "";
      const unit = this.localUnits.find((u) => Number(u.id) === this.effectiveUnitId);
      if (unit?.name || unit?.label) return unit.name || unit.label;
      if (this.measuringUnitName) return this.measuringUnitName;
      if (this.pendingUnitLabel) return this.pendingUnitLabel;
      return `#${this.effectiveUnitId}`;
    },
    filteredUnits() {
      const q = (this.search || "").trim().toLowerCase();
      if (!q) return this.localUnits;
      return this.localUnits.filter((u) =>
        (u.name || u.label || "").toLowerCase().includes(q)
      );
    },
  },
  watch: {
    units: {
      immediate: true,
      deep: true,
      handler(units) {
        this.localUnits = (units || []).map((u) => ({
          ...u,
          name: u.name || u.label || "",
          label: u.label || u.name || "",
        }));
        this.ensureUnitInList();
      },
    },
    measuringUnit() {
      this.ensureUnitInList();
    },
    measuringUnitName() {
      this.ensureUnitInList();
    },
    defaultMeasuringUnit() {
      this.ensureUnitInList();
    },
    open(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.updatePopoverPosition();
          document.addEventListener("mousedown", this.onDocumentPointerDown, true);
        });
        window.addEventListener("scroll", this.updatePopoverPosition, true);
        window.addEventListener("resize", this.updatePopoverPosition);
      } else {
        this.detachOutsideListener();
        window.removeEventListener("scroll", this.updatePopoverPosition, true);
        window.removeEventListener("resize", this.updatePopoverPosition);
        this.cancelCreate();
        this.cancelRename();
        this.search = "";
      }
    },
  },
  beforeUnmount() {
    this.detachOutsideListener();
    window.removeEventListener("scroll", this.updatePopoverPosition, true);
    window.removeEventListener("resize", this.updatePopoverPosition);
  },
  methods: {
    ensureUnitInList() {
      if (!this.effectiveUnitId) return;
      if (this.localUnits.some((u) => Number(u.id) === this.effectiveUnitId)) return;
      const name = this.measuringUnitName || this.pendingUnitLabel || `#${this.effectiveUnitId}`;
      this.localUnits = [
        ...this.localUnits,
        { id: this.effectiveUnitId, name, label: name },
      ];
    },
    togglePopover() {
      if (this.readonly) return;
      this.open = !this.open;
    },
    updatePopoverPosition() {
      const trigger = this.$refs.trigger;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const width = 240;
      const gap = 4;
      let left = rect.right - width;
      if (left < 8) left = 8;
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
      }
      // Prefer below; flip above if not enough space
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const preferBelow = spaceBelow >= 220 || spaceBelow >= rect.top;
      this.popoverStyle = preferBelow
        ? { top: `${rect.bottom + gap}px`, left: `${left}px`, bottom: "auto" }
        : { bottom: `${window.innerHeight - rect.top + gap}px`, left: `${left}px`, top: "auto" };
    },
    detachOutsideListener() {
      document.removeEventListener("mousedown", this.onDocumentPointerDown, true);
    },
    isEventInsidePicker(event) {
      const target = event.target;
      const path = typeof event.composedPath === "function" ? event.composedPath() : [];
      const root = this.$refs.root;
      const popover = this.$refs.popover;
      if (root && (root.contains(target) || path.includes(root))) return true;
      if (popover && (popover.contains(target) || path.includes(popover))) return true;
      return !!target?.closest?.(".unit-popover");
    },
    onDocumentPointerDown(event) {
      if (!this.open) return;
      if (this.isEventInsidePicker(event)) return;
      this.open = false;
    },
    afterActionFormOpen(inputRef) {
      this.$nextTick(() => {
        this.updatePopoverPosition();
        this.$refs[inputRef]?.focus();
      });
    },
    openCreate() {
      this.showRename = false;
      this.showCreate = true;
      this.afterActionFormOpen("newUnitInput");
    },
    notifyError(message) {
      if (this.$toast?.error) {
        this.$toast.error(message);
      } else {
        window.alert(message);
      }
    },
    async selectUnit(unitId) {
      if (this.readonly) {
        this.open = false;
        return;
      }

      if (this.deferSave || !this.categoryId || !this.attributeId) {
        this.$emit("update:measuringUnit", unitId);
        this.open = false;
        return;
      }

      this.saving = true;
      try {
        await AttributeServices.updateCategoryAttributeMeasuringUnit({
          category_id: this.categoryId,
          attribute_id: this.attributeId,
          measuring_unit: unitId,
          category_related_entity: this.categoryRelatedEntity,
        });
        this.$emit("update:measuringUnit", unitId);
        this.open = false;
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Nuk u ruajt njësia matese.";
        this.notifyError(msg);
      } finally {
        this.saving = false;
      }
    },
    cancelCreate() {
      this.showCreate = false;
      this.newUnitName = "";
    },
    async createUnit() {
      const name = (this.newUnitName || "").trim();
      if (!name || this.saving) return;

      this.saving = true;
      try {
        const created = await UnitsServices.createUnit({ name });
        const createdId = created?.id ?? created?.data?.id;
        const createdName = created?.name || created?.data?.name || name;
        if (createdId) {
          this.pendingUnitLabel = createdName;
          this.localUnits.push({ id: createdId, name: createdName, label: createdName });
          this.$emit("units-changed", this.localUnits);
          await this.selectUnit(createdId);
          this.cancelCreate();
        } else {
          this.notifyError("Njesia nuk u krijua. Provo përsëri.");
        }
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Nuk u krijua njësia.";
        this.notifyError(msg);
      } finally {
        this.saving = false;
      }
    },
    startRename() {
      const unit = this.localUnits.find((u) => Number(u.id) === this.effectiveUnitId);
      this.renameUnitName = unit?.name || unit?.label || "";
      this.showCreate = false;
      this.showRename = true;
      this.afterActionFormOpen("renameUnitInput");
    },
    cancelRename() {
      this.showRename = false;
      this.renameUnitName = "";
    },
    async confirmRename() {
      const name = (this.renameUnitName || "").trim();
      if (!name || !this.effectiveUnitId || this.saving) return;

      const confirmed = window.confirm(
        "Riemërtimi i njësisë ndryshon emrin për të gjithë sistemin. Vazhdo?"
      );
      if (!confirmed) return;

      this.saving = true;
      try {
        const updated = await UnitsServices.editUnit({ id: this.effectiveUnitId, name });
        const updatedName = updated?.name || updated?.data?.name || name;
        const idx = this.localUnits.findIndex((u) => Number(u.id) === this.effectiveUnitId);
        if (idx !== -1) {
          this.localUnits[idx].name = updatedName;
          this.localUnits[idx].label = updatedName;
          this.$emit("units-changed", this.localUnits);
        }
        this.cancelRename();
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Nuk u riemërua njësia.";
        this.notifyError(msg);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.attribute-unit-picker {
  position: relative;
  flex-shrink: 0;
}

.unit-trigger {
  display: inline-flex;
  align-items: center;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  background: #f5f5f5;
  color: #363636;
  font-size: 0.8rem;
  padding: 0.25rem 0.45rem;
  cursor: pointer;
  min-width: 64px;
  max-width: 110px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unit-trigger--empty {
  color: #b5b5b5;
  border-style: dashed;
}
</style>

<!-- Teleported popover is outside component DOM; unscoped styles needed for class names -->
<style>
.unit-popover.unit-popover--teleported {
  position: fixed;
  z-index: 10050;
  width: 240px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  padding: 0.5rem;
  pointer-events: auto;
}

.unit-popover.unit-popover--teleported .unit-popover-search {
  margin-bottom: 0.25rem;
}

.unit-popover.unit-popover--teleported .unit-list {
  max-height: 140px;
  overflow-y: auto;
  margin: 0.35rem 0;
}

.unit-popover.unit-popover--teleported .unit-list-item {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 0.3rem 0.4rem;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
}

.unit-popover.unit-popover--teleported .unit-list-item:hover,
.unit-popover.unit-popover--teleported .unit-list-item.active {
  background: #f0f4ff;
  color: #3273dc;
}

.unit-popover.unit-popover--teleported .unit-popover-actions {
  border-top: 1px solid #eee;
  padding-top: 0.4rem;
}

.unit-popover.unit-popover--teleported .unit-create-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}

.unit-popover.unit-popover--teleported .unit-create-row .input {
  flex: 1;
  min-width: 100px;
}

.unit-popover.unit-popover--teleported .unit-rename-row {
  margin-top: 0.35rem;
}

.unit-popover.unit-popover--teleported .unit-global-hint {
  margin-top: 0.25rem;
  color: #7a7a7a;
}
</style>
